import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Tag}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Similar to Badge but with squared/rounded corners instead of pill shape
 * - Supports six variants: primary, secondary, danger, success, warning, muted
 * - Supports three sizes: sm, md (default), lg
 *
 * @example
 * ```tsx
 * const classes = tagVariants({ variant: 'primary', size: 'md' })
 * // Returns: "inline-flex items-center rounded-ds-sm ... bg-ds-primary ..."
 * ```
 */
export const tagVariants = cva('inline-flex items-center justify-center font-normal rounded-ds-sm', {
	variants: {
		variant: {
			primary: 'bg-ds-primary text-ds-primary-foreground',
			secondary: 'bg-ds-secondary text-ds-text',
			danger: 'bg-ds-danger text-ds-danger-foreground',
			success: 'bg-ds-success text-ds-success-foreground',
			warning: 'bg-ds-warning text-ds-warning-foreground',
			muted: 'bg-ds-surface text-ds-text-muted border border-ds-border',
		},
		size: {
			sm: 'text-ds-sm px-2 py-0.5 h-5',
			md: 'text-ds-base px-3 py-1 h-6',
			lg: 'text-ds-lg px-4 py-1.5 h-8',
		},
	},
	defaultVariants: { variant: 'primary', size: 'md' },
})

/**
 * Props for the {@link Tag} component.
 *
 * @property variant - Visual style: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'muted'
 * @property size - Tag size: 'sm' | 'md' (default) | 'lg'
 * @property className - Optional additional Tailwind classes
 * @property children - Tag text content
 *
 * @example
 * ```tsx
 * <Tag variant="primary" size="md">
 *   Category
 * </Tag>
 * ```
 */
export interface TagProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof tagVariants> {}

/**
 * Tag component for categories, labels, and metadata.
 *
 * @remarks
 * - **Styling:** All colors and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Variants:** Primary (brand primary), secondary, danger, success, warning, muted.
 * - **Sizes:** Small (h-5), medium (h-6, default), large (h-8).
 * - **Shape:** Rounded-ds-sm (small border radius, more square than Badge).
 * - **Usage:** Category labels, metadata tags, classification indicators.
 * - **Difference from Badge:** Tags use `rounded-ds-sm` (square), Badges use `rounded-ds-full` (pill).
 *
 * @example Basic usage
 * ```tsx
 * import { Tag } from '@atlas/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <div>
 *       <Tag variant="primary">Category</Tag>
 *       <Tag variant="secondary">Technology</Tag>
 *       <Tag variant="muted">Draft</Tag>
 *     </div>
 *   )
 * }
 * ```
 *
 * @example With different sizes
 * ```tsx
 * <Tag size="sm" variant="muted">Small</Tag>
 * <Tag size="md">Medium</Tag>
 * <Tag size="lg" variant="success">Large</Tag>
 * ```
 *
 * @see {@link tagVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/tag/tag.stories.tsx for visual examples
 */
export function Tag({ variant, size, className, ...props }: TagProps) {
	return <span {...props} className={cn(tagVariants({ variant, size }), className)} />
}
